import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchSuggestions,
  createSuggestion,
  updateSuggestion,
  deleteSuggestion,
  markAsRead,
  clearError,
} from 'store/slices/suggestionSlice';
import { Loader2, CheckCircle, Pencil, Trash2, MoreHorizontal, MoreVertical } from 'lucide-react';
import DataTable from '../../components/common/DataTable';
import PageHeader from '../../components/common/PageHeader';
import FormField from '../../components/common/FormField';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../../components/ui/dropdown-menu';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '../../components/ui/alert-dialog';
import { usePermissions } from 'hooks/usePermissions';
import usePermissionSocket from 'hooks/usePermissionSocket';
import { formatDateTime } from 'utlis/formateDateTime';

const SuggestionPage = () => {
  usePermissionSocket();
  const dispatch = useDispatch();

  const { suggestions, pagination, isLoading, error } = useSelector((state) => state.suggestion);
  const { canCreate, canUpdate, canDelete, canRead } = usePermissions('suggestions');

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [currentSuggestion, setCurrentSuggestion] = useState(null);
  const [formData, setFormData] = useState({ suggestion: '', is_it_related: true });
  const [formErrors, setFormErrors] = useState({});
    // Add a state to hold the suggestion to show in modal
  const [fullSuggestion, setFullSuggestion] = useState(null);
  const [isSuggestionModalOpen, setIsSuggestionModalOpen] = useState(false);

  // Function to truncate text by words
  const truncateWords = (text, wordLimit) => {
    const words = text.split(' ');
    if (words.length <= wordLimit) return text;
    return words.slice(0, wordLimit).join(' ') + '...';
  };



  const refetch = useCallback(() => {
    dispatch(fetchSuggestions({ page, pageSize, search: searchTerm }));
  }, [dispatch, page, pageSize, searchTerm]);

  useEffect(() => refetch(), [refetch]);

  useEffect(() => {
    if (error) {
      toast.error(typeof error === 'string' ? error : 'An error occurred');
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const validate = () => {
    const errors = {};
    if (!formData.suggestion.trim()) errors.suggestion = 'Suggestion is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const openDialog = (sugg = null) => {
    if (sugg && !canUpdate) return toast.error("No permission to edit");
    if (!sugg && !canCreate) return toast.error("No permission to create");

    setCurrentSuggestion(sugg);
    setFormData({
      suggestion: sugg?.suggestion || '',
      is_it_related: sugg?.is_it_related ?? true,
    });
    setFormErrors({});
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      if (currentSuggestion) {
        await dispatch(updateSuggestion({ id: currentSuggestion.id, ...formData })).unwrap();
        toast.success('Suggestion updated');
      } else {
        await dispatch(createSuggestion(formData)).unwrap();
        toast.success('Suggestion submitted');
      }
      refetch();
      setIsDialogOpen(false);
    } catch { }
  };

  const handleDelete = async () => {
    if (!canDelete) return toast.error("No permission");
    try {
      await dispatch(deleteSuggestion(currentSuggestion.id)).unwrap();
      toast.success('Suggestion deleted');
      refetch();
      setIsDeleteOpen(false);
    } catch { }
  };

  const handleMarkRead = async (id) => {
    try {
      await dispatch(markAsRead(id)).unwrap();
      toast.success('Marked as read');

      // This line fixes the issue → fresh data from backend
      refetch();
    } catch (err) {
      toast.error('Failed to mark as read');
    }
  };
  const columns = [
    {
      accessorKey: 'employee_name',
      header: 'Employee Name',
      cell: ({ row }) => <span className="font-medium">{row.original.employee_name}</span>,
    },
  {
      accessorKey: 'suggestion',
      header: 'Suggestions',
      cell: ({ row }) => {
        const suggestion = row.original.suggestion;
        const truncated = truncateWords(suggestion, 10); // Show first 10 words

        return (
          <button
        onClick={() => {
          setFullSuggestion(suggestion);
          setIsSuggestionModalOpen(true);
        }}
        className="text-sm text-primary underline decoration-dotted underline-offset-2 hover:text-primary/80 focus:outline-none"
        title="Click to view full suggestion"
        style={{ maxWidth: '200px', display: 'block', whiteSpace: 'normal', overflowWrap: 'break-word', textAlign: 'left' }}
      >
        {truncated}
      </button>
        );
      },
    },

    {
      accessorKey: 'is_it_related',
      header: 'IT Related',
      cell: ({ row }) => (
        <Badge variant={row.original.is_it_related ? 'success' : 'secondary'}>
          {row.original.is_it_related ? 'Yes' : 'No'}
        </Badge>
      ),
    },
    {
      accessorKey: 'created_at',
      header: 'Submitted On',
      cell: ({ row }) => (formatDateTime(row.original.created_at)),
    },
    {
      accessorKey: 'is_read',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={row.original.is_read ? 'secondary' : 'default'}>
          {row.original.is_read ? 'Read' : 'New'}
        </Badge>
      ),
    },
    {
      accessorKey: 'updated_by_name',
      header: 'Read By',
      cell: ({ row }) => row.original.updated_by_name || '-',
    },

    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const s = row.original;
        return (
          <div className="flex items-center gap-2">
            {!s.is_read && canUpdate && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleMarkRead(s.id)}
                className="h-8 text-xs"
              >
                <CheckCircle className="h-3.5 w-3.5 mr-1" />
                Mark Read
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {canUpdate && (
                  <DropdownMenuItem onClick={() => openDialog(s)}>
                    <Pencil className="h-4 w-4 mr-2" /> Edit
                  </DropdownMenuItem>
                )}
                {canDelete && (
                  <DropdownMenuItem
                    onClick={() => {
                      setCurrentSuggestion(s);
                      setIsDeleteOpen(true);
                    }}
                    className="text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-2" /> Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Employee Suggestions"
        description="View and manage suggestions from team members"
        action={canCreate ? { label: 'New Suggestion', onClick: () => openDialog() } : null}
      />

      {/* {isLoading && !suggestions.length && (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          <span className="text-muted-foreground">Loading suggestions...</span>
        </div>
      )} */}

      {/* {canRead ? ( */}
      <DataTable
        data={suggestions}
        totalCount={pagination.count}
        columns={columns}
        searchPlaceholder="Search suggestions..."
        pageIndex={page - 1}
        pageSize={pageSize}
        pageCount={Math.ceil(pagination.count / pagination.page_size)}
        onPageChange={(i) => setPage(i + 1)}
        onPageSizeChange={setPageSize}
        onSearch={setSearchTerm}
        isLoading={isLoading}
        showExport={false}
        showImport={false}
        onDelete={async (id) => {
          await dispatch(deleteSuggestion(id));
          refetch();
        }}
        onRestore={async (id) => {
          await dispatch(updateSuggestion({ id, is_deleted: false }));
          refetch();
        }}
      />
      {/* ) */}
      {/* : (
        <p className="text-center text-muted-foreground py-12">You don't have permission to view suggestions.</p>
      )} */}

      {/* FORM DIALOG */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{currentSuggestion ? 'Edit Suggestion' : 'Submit Suggestion'}</DialogTitle>
            <DialogDescription>
              {currentSuggestion ? 'Update the suggestion' : 'Share your feedback to improve the system'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5">
            <FormField
              label="Your Suggestion"
              name="suggestion"
              type="textarea"
              value={formData.suggestion}
              onChange={(e) => setFormData({ ...formData, suggestion: e.target.value })}
              error={formErrors.suggestion}
              required
              rows={6}
              placeholder="Write your suggestion here..."
            />
            <FormField
              label="IT Related?"
              name="is_it_related"
              type="switch"
              checked={formData.is_it_related}
              onCheckedChange={(v) => setFormData({ ...formData, is_it_related: v })}
            />
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {currentSuggestion ? 'Update' : 'Submit'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* DELETE CONFIRM */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Suggestion?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>



       {/* Modal for full suggestion */}
      <Dialog open={isSuggestionModalOpen} onOpenChange={setIsSuggestionModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Full Suggestion</DialogTitle>
          </DialogHeader>
          <div className="p-4 text-sm whitespace-pre-wrap">{fullSuggestion}</div>
          <div className="flex justify-end pt-4">
            <Button onClick={() => setIsSuggestionModalOpen(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuggestionPage;